<template>
  <div class="fiche-vo-pdf-strict">

    <div class="row-header">
      <div class="col-title">
        <div class="meta-row">
          <span class="badge-occasion">{{ $t('posts.view.pdfVoRefPrefix') }}{{ post.reference }}</span>
        </div>
        <h1 class="main-title">{{ titreVitrine }}</h1>
      </div>
      <div class="col-price">
        <div class="price-value">{{ post.prixDisplay }}</div>
        <div class="price-sub" v-html="$t('posts.view.pdfVoPriceDisclaimerPortrait')"></div>
      </div>
    </div>

    <div class="content-wrapper">

      <div class="row-hero">
        <div class="col-photo">
          <div class="image-wrapper">
            <img
                v-if="post.photoPrincipale && post.photoPrincipale.url"
                :src="post.photoPrincipale.url"
                :alt="$t('posts.view.pdfVoMainPhotoAlt')"
                class="hero-image"
            />
            <div v-else class="hero-no-image">
              <ubi-icon name="camera" size="80"></ubi-icon>
            </div>

            <div v-if="garantieBadgeText" class="badge-garantie">{{ garantieBadgeText }}</div>
          </div>
        </div>

        <div class="col-contact-hero">
          <div class="contact-card">
            <div class="contact-line"></div>
            <div class="contact-agency-name">{{ sitimmoUser.raisonSociale }}</div>
            <div class="contact-agency-address">
              <ubi-icon name="map-marker" size="12"></ubi-icon>
              {{ sitimmoUser.adresse }} - {{ sitimmoUser.codePostal }} {{ sitimmoUser.ville }}
            </div>

            <div class="contact-btn">
              <div class="btn-icon">
                <i class="mdi mdi-phone"></i>
              </div>
              <div class="btn-text">
                <div class="btn-label">{{ $t('posts.view.pdfVoPhoneLabel') }}</div>
                <div class="btn-val">{{ sitimmoUser.telephone }}</div>
              </div>
            </div>

            <div class="contact-btn" v-if="sitimmoUser.email">
              <div class="btn-icon">
                <i class="mdi mdi-email-outline"></i>
              </div>
              <div class="btn-text">
                <div class="btn-label">{{ $t('posts.view.pdfVoEmailLabel') }}</div>
                <div class="btn-val" style="font-size: 11px;">{{ sitimmoUser.email }}</div>
              </div>
            </div>

            <div class="contact-footer">
              <div>{{ $t('posts.view.pdfVoContactFooterLine1') }}</div>
              <div>{{ $t('posts.view.pdfVoContactFooterLine2') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row-specs">
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-calendar spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.pdfVoFirstRegistrationShort') }}</div>
          <div class="spec-val">{{ post.mise_en_circulation || '-' }}</div>
        </div>
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-speedometer spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.pdfVoMileageLabel') }}</div>
          <div class="spec-val">{{ post.kilometrage || '-' }} km</div>
          <div class="spec-hint">{{ post.kilometrage_garanti ? $t('posts.view.pdfVoMileageGuaranteedShort') : $t('posts.view.pdfVoMileageNotGuaranteedShort') }}</div>
        </div>
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-gas-station spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.energy') }}</div>
          <div class="spec-val">{{ post.energie || '-' }}</div>
          <div class="spec-hint spec-hint-rechargeable" v-if="hybrideRechargeableLabel">{{ hybrideRechargeableLabel }}</div>
        </div>
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-cog spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.transmission') }}</div>
          <div class="spec-val">{{ post.boite_de_vitesse || '-' }}</div>
        </div>
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-lightning-bolt spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.power') }}</div>
          <div class="spec-val" v-if="post.puissance_reelle || post.puissance_fiscale">{{ post.puissance_reelle }} {{ $t('posts.view.pdfVoHorsepowerUnit') }}</div>
          <div class="spec-val" v-else>-</div>
        </div>
        <div class="spec-box">
          <div class="spec-icon">
            <i class="mdi mdi-leaf spec-icon-img"></i>
          </div>
          <div class="spec-label">{{ $t('posts.view.vignette') }}</div>
          <div class="spec-val">{{ $t('posts.view.pdfVoCritairLabel') }} {{ post.classe_critair || '-' }}</div>
        </div>
      </div>

      <div class="row-body" v-if="hasEquipmentsSection">
        <div class="col-equipments">
          <h2 class="section-title">{{ $t('posts.view.equipmentsAndOptions') }}</h2>

          <div v-if="qualifiedEquipments.length" class="equip-grid" :class="{ 'compact-mode': qualifiedEquipments.length > 24 }">
            <div v-for="(equip, idx) in qualifiedEquipments" :key="'eq-'+idx" class="equip-item">
              <span class="equip-check">✓</span> <span class="equip-text">{{ equip }}</span>
            </div>
          </div>

          <div v-if="unqualifiedEquipmentsText" class="unqualified-equipments-text">
            <strong>{{ $t('posts.edit.otherEquipements') || 'Autres équipements :' }}</strong> {{ unqualifiedEquipmentsText }}
          </div>

        </div>
      </div>

    </div>

  </div>
</template>

<script>
import UbiIcon from '@/components/Icon.vue'
import { getTitreAlternatif } from '@/helpers/posts/getTitreAlternatif'
import { getPdfVoGarantieBadgeText } from '@/helpers/posts/getPdfVoGarantieBadge'
import { hasHybrideRechargeable, shortHybrideRechargeableLabel } from '@/helpers/posts/hasHybrideRechargeable'

export default {
  name: 'PostPdfVo',
  components: { UbiIcon },
  props: ['post', 'sitimmoUser', 'universe'],
  methods: {
    normalizeRawEquipments (value) {
      if (value === null || value === undefined) return ''
      const normalized = String(value).trim()
      if (!normalized) return ''
      const lower = normalized.toLowerCase()
      if (lower === 'null' || lower === 'undefined') return ''
      return normalized
    },
    getFormattedUnqualifiedEquipments (maxLength) {
      const raw = this.normalizeRawEquipments(this.post.equipements_non_qualifies)
      if (!raw) return ''

      const items = raw.split(/[,;]/)
        .map(s => s.trim())
        .filter(s => s.length > 0)

      if (!items.length) return ''

      let text = ''
      let isTruncated = false

      for (const item of items) {
        const candidate = text ? `${text}, ${item}` : item
        if (candidate.length > maxLength) {
          isTruncated = true
          break
        }
        text = candidate
      }

      if (!text) {
        text = items[0].slice(0, maxLength)
        isTruncated = true
      }

      if (!isTruncated && text.length < raw.trim().length) {
        isTruncated = true
      }

      return `${text}...`
    }
  },
  computed: {
    titreVitrine () {
      return getTitreAlternatif(this.post)
    },
    qualifiedEquipments () {
      const raw = this.normalizeRawEquipments(this.post.equipments)
      if (!raw) return []
      return raw.split(/[,;]/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
    },
    unqualifiedEquipmentsText () {
      // Augmentee pour exploiter l'espace restant avant saut de page.
      return this.getFormattedUnqualifiedEquipments(1800)
    },
    hasEquipmentsSection () {
      return this.qualifiedEquipments.length > 0 || !!this.unqualifiedEquipmentsText
    },
    garantieBadgeText () {
      return getPdfVoGarantieBadgeText(this.post, {
        monthUnit: this.$t('posts.view.pdfVoWarrantyMonthUnit'),
        prefix: this.$t('posts.view.pdfVoMileageGuaranteedShortTitle')
      })
    },
    hybrideRechargeableLabel () {
      if (!hasHybrideRechargeable(this.post)) return ''
      const labels = this.$labels
      return shortHybrideRechargeableLabel(labels && labels.hybride_rechargeable)
    }
  }
}
</script>

<style scoped>
.fiche-vo-pdf-strict {
  width: 794px !important;
  min-width: 794px !important;
  max-width: 794px !important;
  min-height: 1123px !important;
  background-color: #ffffff;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #154c59;
  display: block;
  box-sizing: border-box;
  position: relative;
  /* Marge horizontale unique : gauche = droite sur toute la fiche */
  padding-left: 40px;
  padding-right: 40px;
  padding-bottom: 30px;
  margin: 0 auto;
}

/* MODIFICATION HEADER */
.row-header {
  display: flex;
  width: 100%;
  align-items: flex-start;
  min-height: 90px; /* Assure que le fond bleu va jusqu'en bas du titre proprement */
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 5px; /* Moins d'espace en dessous */
}

.col-title {
  flex: 1;
  padding: 15px 0 10px 0; /* Ajustement fin pour le trait */
  box-sizing: border-box;
}

.meta-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.badge-occasion {
  background-color: #154c59;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.main-title {
  font-size: 28px;
  font-weight: 800;
  color: #154c59;
  margin: 5px 0 0 0;
  line-height: 1.1;
  text-transform: uppercase;
}

.col-price {
  width: 45%;
  background-color: #2cb1ba;
  color: #ffffff;
  border-radius: 0 0 15px 15px;
  padding: 15px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}

.price-value {
  font-size: 50px;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 5px;
}

.price-sub {
  font-size: 9px;
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
}

.content-wrapper {
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}

.row-hero {
  width: 100%;
  height: 270px;
  padding-top: 15px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 20px;
}

.col-photo {
  width: 62%;
  height: 100%;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f4f6f8;
  padding: 20px 15px 15px 15px;
  box-sizing: border-box;
}

.hero-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: none !important;
  outline: none !important;
}

.hero-no-image {
  color: #bdc3c7;
}

.badge-garantie {
  position: absolute;
  bottom: 15px;
  left: 15px;
  background-color: #154c59;
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
  z-index: 10;
}

.col-contact-hero {
  width: 35%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.row-specs {
  display: flex;
  width: 100%;
  height: 86px;
  border-bottom: 1px solid #e0e0e0;
  background: #fdfdfd;
  margin-top: 15px;
  box-sizing: border-box;
}

.spec-box {
  width: 16.66%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-right: 1px solid #f0f0f0;
  padding-top: 8px;
  box-sizing: border-box;
}

.spec-box:last-child {
  border-right: none;
}

.spec-icon {
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-bottom: 6px;
  width: 100%;
}

.spec-icon-img {
  font-size: 22px;
  color: #2cb1ba;
  display: block;
}

.spec-label {
  font-size: 9px;
  color: #95a5a6;
  text-transform: uppercase;
  font-weight: bold;
  height: 12px;
  margin-bottom: 2px;
  text-align: center;
  display: block;
}

.spec-val {
  font-size: 12px;
  font-weight: 800;
  color: #154c59;
  text-align: center;
  height: 15px;
  display: block;
}

.spec-hint {
  font-size: 8px;
  color: #95a5a6;
  font-style: italic;
  margin-top: 2px;
  text-align: center;
  height: 10px;
  display: block;
}

.spec-hint-rechargeable {
  font-size: 8px;
  font-weight: 700;
  font-style: italic;
  line-height: 10px;
  height: 10px;
  margin-bottom: 4px;
}

.row-body {
  display: block;
  width: 100%;
  padding-top: 15px;
  padding-bottom: 20px;
  box-sizing: border-box;
}

.col-equipments {
  width: 100%;
}

.section-title {
  font-size: 16px;
  font-weight: 800;
  color: #154c59;
  margin: 0 0 15px 0;
  text-transform: uppercase;
}

/* MODIFICATION GRILLE 3 COLONNES */
.equip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  column-gap: 15px;
  row-gap: 12px;
  width: 100%;
}

.equip-item {
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  font-size: 11px;
  font-weight: 600;
  color: #34495e;
  line-height: 1.2;
  page-break-inside: avoid;
  break-inside: avoid;
}

.equip-check {
  color: #2cb1ba;
  border: 1px solid #2cb1ba;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 9px;
  margin-right: 8px;
  flex-shrink: 0;
}

.equip-grid.compact-mode {
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  column-gap: 10px;
  row-gap: 8px;
}
.equip-grid.compact-mode .equip-text {
  font-size: 9.5px;
  line-height: 1.1;
}
.equip-grid.compact-mode .equip-check {
  width: 12px;
  height: 12px;
  font-size: 8px;
  margin-right: 6px;
}

/* MODIFICATION TRONCATURE AUTRES EQUIPEMENTS */
.unqualified-equipments-text {
  margin-top: 15px;
  padding-top: 12px;
  border-top: 1px dashed #ecf0f1;
  font-size: 10px;
  color: #7f8c8d;
  line-height: 1.5;
  text-align: justify;
}
.unqualified-equipments-text strong {
  color: #154c59;
}

.contact-card {
  background-color: #154c59;
  border-radius: 20px;
  padding: 15px 20px;
  color: white;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.contact-line {
  width: 40px;
  height: 4px;
  background-color: #2cb1ba;
  margin-bottom: 8px;
}

.contact-agency-name {
  color: #2cb1ba;
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 5px;
}

.contact-agency-address {
  font-size: 11px;
  color: #ecf0f1;
  margin-bottom: 12px;
  line-height: 1.4;
}

.contact-btn {
  background-color: #1f606b;
  border-radius: 12px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.contact-footer {
  font-size: 8px;
  text-align: center;
  color: #7f8c8d;
  margin-top: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.btn-icon {
  background-color: #2cb1ba;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
}

.btn-icon i {
  font-size: 16px;
  color: #ffffff;
}

.btn-label {
  font-size: 8px;
  letter-spacing: 1px;
  color: #bdc3c7;
  text-transform: uppercase;
}

.btn-val {
  font-size: 14px;
  font-weight: bold;
  margin-top: 2px;
}
</style>
